import type { AIOperation, AIResponse, ApiResponse, MindMapNode } from '@bandos/shared'
import type { SourceNodeInfo, AIStateSetters, AIRefs } from '../types/aiTypes'
import { addSuggestionsToUI, startOperationUI, setErrorInUI } from './aiUIStateHelpers'

// ============================================================================
// TYPES
// ============================================================================

interface ExecutorConfig {
  mindmapId: string | undefined
  waitForPendingSaves: () => Promise<void>
}

interface ExecutorCallbacks {
  onSuccess?: (operation: AIOperation, result: AIResponse) => void
  onError?: (operation: AIOperation, error: Error | string) => void
  onReady?: (nodeId: string, count: number) => void
  onRegenerateFeedbackForChildren?: (sourceNodeId: string) => void
}

export interface ExecuteAIOperationResult {
  success: boolean
  data?: AIResponse | null
  error?: unknown
}

// ============================================================================
// CORE EXECUTOR
// ============================================================================

/**
 * Executes an AI operation end-to-end:
 * 1. Validates mindmapId
 * 2. Updates UI to loading state
 * 3. Waits for pending saves
 * 4. Calls the AI service
 * 5. Handles success/error and updates UI
 */
export async function executeAIOperation(
  operation: AIOperation,
  nodeId: string,
  serviceCall: () => Promise<ApiResponse<AIResponse>>,
  node: MindMapNode | undefined,
  preserveSuggestions: boolean,
  userFeedback: string | undefined,
  config: ExecutorConfig,
  setters: AIStateSetters,
  refs: AIRefs,
  sourceNodes: Map<string, SourceNodeInfo>,
  callbacks: ExecutorCallbacks
): Promise<ExecuteAIOperationResult> {
  if (!config.mindmapId) {
    const errorMsg = 'MindmapId not available'
    callbacks.onError?.(operation, new Error(errorMsg))
    setErrorInUI(errorMsg, nodeId, setters)
    return { success: false, error: errorMsg }
  }

  try {
    setters.setIsProcessing(true)
    setters.setCurrentOperation(operation)
    startOperationUI(operation, nodeId, node, preserveSuggestions, userFeedback, setters)

    await config.waitForPendingSaves()
    const result = await serviceCall()

    if (result.error) {
      const errorMsg = result.error.message || 'An error occurred while generating suggestions'
      callbacks.onError?.(operation, errorMsg)
      setErrorInUI(errorMsg, nodeId, setters)
      return { success: false, error: result.error }
    }

    if (result.data && result.data.nodes && result.data.edges) {
      addSuggestionsToUI(
        operation,
        result.data.nodes,
        result.data.edges,
        nodeId,
        preserveSuggestions,
        result.data.suggestionIds,
        userFeedback,
        setters,
        refs,
        sourceNodes,
        {
          onReady: callbacks.onReady,
          onRegenerateFeedbackForChildren: callbacks.onRegenerateFeedbackForChildren,
        }
      )
    }

    if (result.data) {
      callbacks.onSuccess?.(operation, result.data)
    }
    return { success: true, data: result.data }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error)
    callbacks.onError?.(operation, errorMsg)
    setErrorInUI(errorMsg, nodeId, setters)

    return { success: false, error: errorMsg }
  } finally {
    setters.setPendingOperations((prev) => {
      const updated = new Set(prev)
      updated.delete(operation)
      if (updated.size === 0) {
        setters.setIsProcessing(false)
      }
      return updated
    })
  }
}
